import React, { useEffect, useState } from 'react';
import MatchSheet from './MatchSheet';
import injuryIcon from '/src/assets/messages/injury.png';
import { formatPlayerName } from '../../utils/playerName';

const LOW_FITNESS_THRESHOLD = 65;
// The countdown is display only: the engine owns the real deadline and always
// closes this sheet with simulation_resumed. Ticking faster than once a second
// only keeps the draining bar smooth.
const INJURY_COUNTDOWN_TICK_MS = 250;

// Engine-sourced names arrive underscore-joined; display with normal spacing.
const getPlayerLabel = (player) => (
    formatPlayerName(player?.shortName || player?.name || 'Unknown')
);

const getPlayerPosition = (player) => String(player?.position || player?.currentPosition || '').toUpperCase();

/**
 * Live in-match rating streamed by the engine. Deliberately NOT the static
 * ability rating that also travels on the player object: showing that one
 * would look like a performance score and mislead.
 */
const getLiveRating = (player) => {
    const liveRating = Number(player?.live_rating ?? player?.liveRating);
    return Number.isFinite(liveRating) ? liveRating.toFixed(1) : null;
};

/**
 * Engine fatigue is already normalised so 100 means fresh.
 */
const getFitness = (player) => {
    const fatigue = Number(player?.fatigue);
    if (!Number.isFinite(fatigue)) return null;
    return Math.min(100, Math.max(0, Math.round(fatigue)));
};

function PlayerOption({ player, isSelected, onSelect }) {
    const fitness = getFitness(player);
    const rating = getLiveRating(player);

    return (
        <button
            type="button"
            className={`mx-popt${isSelected ? ' is-selected' : ''}`}
            onClick={() => onSelect(player.id)}
            disabled={player.disabled === true}
            aria-pressed={isSelected}
            aria-label={getPlayerLabel(player)}
        >
            <span className="mx-popt__pos">{getPlayerPosition(player) || '--'}</span>
            <span className="mx-popt__main">
                <span className="mx-popt__name">{getPlayerLabel(player)}</span>
                {fitness !== null && (
                    <span className="mx-popt__fitness" aria-hidden="true">
                        <i
                            className={fitness < LOW_FITNESS_THRESHOLD ? 'is-low' : undefined}
                            style={{ width: `${fitness}%` }}
                        />
                    </span>
                )}
            </span>
            {rating !== null && <span className="mx-popt__rating">{rating}</span>}
        </button>
    );
}

/**
 * Substitution sheet. Two columns of tap targets instead of the old pair of
 * dropdowns, with the live rating and fitness bar shown when the engine sends
 * them (both optional: older matches simply do not render them).
 *
 * `injuryContext` switches the sheet into injury mode: the injured player is
 * pinned as the outgoing player, a countdown shows how long the engine will
 * wait before it substitutes for you, and the footer offers the play on escape
 * hatch.
 */
function SubstitutionSheet({
    isOpen,
    onClose,
    activePlayers = [],
    substitutionsPlayers = [],
    onConfirm,
    substitutionsUsed = 0,
    maxSubstitutions = 3,
    isLiveMatch = false,
    isSubmitting = false,
    injuryContext = null,
    onPlayOn
}) {
    const [playerOutId, setPlayerOutId] = useState(null);
    const [playerInId, setPlayerInId] = useState(null);
    const [injuryMsLeft, setInjuryMsLeft] = useState(0);

    const injuryOutPlayerId = injuryContext?.outPlayerId ?? null;
    const injuryTotalSeconds = Math.max(0, Number(injuryContext?.autoResumeSeconds) || 0);
    const injuryDeadlineMs = injuryContext
        ? Number(injuryContext.startedAtClientMs) + injuryTotalSeconds * 1000
        : null;

    useEffect(() => {
        if (!isOpen) {
            setPlayerOutId(null);
            setPlayerInId(null);
        }
    }, [isOpen]);

    // A new injury takes over whatever selection was already on screen.
    useEffect(() => {
        if (injuryOutPlayerId === null) return;
        setPlayerOutId(injuryOutPlayerId);
        setPlayerInId(null);
    }, [injuryOutPlayerId]);

    useEffect(() => {
        if (!isOpen || injuryDeadlineMs === null || !Number.isFinite(injuryDeadlineMs)) {
            setInjuryMsLeft(0);
            return undefined;
        }

        const tick = () => {
            setInjuryMsLeft(Math.max(0, injuryDeadlineMs - Date.now()));
        };

        tick();
        const intervalId = setInterval(tick, INJURY_COUNTDOWN_TICK_MS);
        return () => clearInterval(intervalId);
    }, [isOpen, injuryDeadlineMs]);

    const remaining = Math.max(0, maxSubstitutions - substitutionsUsed);
    const hasNoSubstitutionsLeft = isLiveMatch && remaining <= 0;
    // Injury mode pins the outgoing player, so the pinned id wins even on the
    // first render, before the reset effect has run.
    const effectivePlayerOutId = injuryOutPlayerId ?? playerOutId;
    const isConfirmDisabled = isSubmitting || !effectivePlayerOutId || !playerInId || hasNoSubstitutionsLeft;

    const playerOut = activePlayers.find((player) => player.id === effectivePlayerOutId);
    const playerIn = substitutionsPlayers.find((player) => player.id === playerInId);

    const injuredPlayerLabel = injuryContext
        ? (playerOut ? getPlayerLabel(playerOut) : formatPlayerName(injuryContext.playerName || 'Player'))
        : null;
    const injuredPlayerPosition = injuryContext
        ? (getPlayerPosition(playerOut) || String(injuryContext.position || '').toUpperCase())
        : '';
    // Whole seconds for the number, raw milliseconds for the bar, so the bar
    // drains continuously instead of stepping once a second.
    const injurySecondsLeft = Math.ceil(injuryMsLeft / 1000);
    const injuryDrainPercent = injuryTotalSeconds > 0
        ? Math.max(0, Math.min(100, (injuryMsLeft / (injuryTotalSeconds * 1000)) * 100))
        : 0;

    const confirmLabel = isSubmitting
        ? 'SAVING SUBSTITUTION'
        : hasNoSubstitutionsLeft
            ? 'NO SUBSTITUTIONS LEFT'
            : 'MAKE SUBSTITUTION';

    const selectionSummary = playerOut && playerIn
        ? `${getPlayerLabel(playerIn)} on for ${getPlayerLabel(playerOut)}`
        : injuryContext
            ? 'Pick a replacement from the bench'
            : 'Select one player out and one player in';

    return (
        <MatchSheet
            isOpen={isOpen}
            onClose={onClose}
            title={injuryContext ? 'INJURY STOPPAGE' : 'MAKE A SUBSTITUTION'}
            meta={isLiveMatch ? `${remaining} of ${maxSubstitutions} left` : null}
            popupClassName="substitution-popup"
            footer={(
                <>
                    <button
                        type="button"
                        className="mx-confirm-btn"
                        onClick={() => {
                            if (!isConfirmDisabled) onConfirm(effectivePlayerOutId, playerInId);
                        }}
                        disabled={isConfirmDisabled}
                        aria-disabled={isConfirmDisabled}
                        aria-label={confirmLabel}
                    >
                        <span className="mx-confirm-btn__label">{confirmLabel}</span>
                        <span className="mx-confirm-btn__sub">{selectionSummary}</span>
                    </button>
                    {injuryContext && (
                        <button
                            type="button"
                            className="mx-playon-btn"
                            onClick={() => onPlayOn?.()}
                            disabled={isSubmitting}
                            aria-disabled={isSubmitting}
                            data-testid="injury-play-on"
                        >
                            Play on with 10 men
                        </button>
                    )}
                </>
            )}
        >
            {injuryContext && (
                <div className="mx-injury" role="status" data-testid="injury-banner">
                    <span className="mx-injury__icon" aria-hidden="true">
                        <img src={injuryIcon} alt="" />
                    </span>
                    <div className="mx-injury__copy">
                        <p className="mx-injury__title">{injuredPlayerLabel} can't continue</p>
                        <p className="mx-injury__sub">
                            {`Pick a replacement or play on. Auto-sub in ${injurySecondsLeft}s.`}
                        </p>
                    </div>
                    <span className="mx-injury__count" data-testid="injury-countdown">{injurySecondsLeft}</span>
                    <span className="mx-injury__bar" aria-hidden="true">
                        <i style={{ width: `${injuryDrainPercent}%` }} />
                    </span>
                </div>
            )}
            <div className="mx-subcols">
                <div data-testid="sub-column-out">
                    <h6 className="mx-subcols__head">
                        <span className="mx-subcols__dot mx-subcols__dot--off" aria-hidden="true" />
                        PLAYER OUT
                    </h6>
                    {injuryContext ? (
                        <div className="mx-popt mx-popt--pinned" data-testid="injury-pinned-player">
                            <span className="mx-popt__pos">{injuredPlayerPosition || '--'}</span>
                            <span className="mx-popt__main">
                                <span className="mx-popt__name">{injuredPlayerLabel}</span>
                                <span className="mx-popt__tag">INJURED</span>
                            </span>
                        </div>
                    ) : (
                        activePlayers.map((player) => (
                            <PlayerOption
                                key={player.id}
                                player={player}
                                isSelected={player.id === playerOutId}
                                onSelect={setPlayerOutId}
                            />
                        ))
                    )}
                </div>
                <div data-testid="sub-column-in">
                    <h6 className="mx-subcols__head">
                        <span className="mx-subcols__dot mx-subcols__dot--on" aria-hidden="true" />
                        PLAYER IN
                    </h6>
                    {substitutionsPlayers.map((player) => (
                        <PlayerOption
                            key={player.id}
                            player={player}
                            isSelected={player.id === playerInId}
                            onSelect={setPlayerInId}
                        />
                    ))}
                </div>
            </div>
        </MatchSheet>
    );
}

export default SubstitutionSheet;
